import type { FormEventHandler, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export type FormSection = {
  id: number;
  label: string;
  content: ReactNode;
};

type SectionedFormProps = {
  formId: string;
  sections: FormSection[];
  activeSectionId: number;
  onSectionChange: (id: number) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const SectionedForm: React.FC<SectionedFormProps> = ({
  formId,
  sections,
  activeSectionId,
  onSectionChange,
  onSubmit,
}) => (
  <>
    <Tabs
      value={activeSectionId}
      onValueChange={(value) => onSectionChange(value as number)}
    >
      <TabsList variant="line" className="tw:w-full">
        {sections.map((section) => (
          <TabsTrigger key={section.id} value={section.id}>
            {section.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
    <form
      id={formId}
      className="tw:grid tw:max-h-[60vh] tw:gap-2 tw:overflow-y-auto"
      onSubmit={onSubmit}
    >
      {sections.map((section) => (
        <Collapsible
          key={section.id}
          open={activeSectionId === section.id}
          onOpenChange={(open) => open && onSectionChange(section.id)}
          className="tw:rounded-md tw:border tw:border-border"
        >
          <CollapsibleTrigger className="tw:group tw:flex tw:w-full tw:items-center tw:justify-between tw:bg-transparent tw:p-3 tw:font-medium tw:text-foreground">
            {section.label}
            <ChevronDown className="tw:size-4 tw:transition-transform tw:group-data-panel-open:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent keepMounted className="tw:px-3 tw:pb-3">
            {section.content}
          </CollapsibleContent>
        </Collapsible>
      ))}
    </form>
  </>
);

export default SectionedForm;
